import type { AbsenceKind, AbsenceStatus, BillingType, EntryStatus, Language } from './types';

/**
 * Labels used in generated files (Excel, CSV). UI texts live in apps/web/src/i18n.
 * Billing documents for customers exist in German and English; personal exports also in Turkish.
 */

export interface ReportLabels {
  date: string;
  week: string;
  weekday: string;
  projectNumber: string;
  project: string;
  taskId: string;
  task: string;
  description: string;
  hours: string;
  billingType: string;
  status: string;
  location: string;
  person: string;
  total: string;
  sum: string;
  perDay: string;
  perProject: string;
  perArea: string;
  area: string;
  areaGroup: string;
  projectWork: string;
  internalWork: string;
  entries: string;
  overview: string;
  period: string;
  createdAt: string;
  // billing documents
  stundennachweis: string;
  customer: string;
  consultant: string;
  partner: string;
  site: string;
  activity: string;
  totalHours: string;
  rate: string;
  /** hours without a stored sales rate */
  noRate: string;
  amount: string;
  net: string;
  vat: string;
  gross: string;
  /** payment terms on an invoice document */
  paymentTerms: string;
  payable: string;
  discount: string;
  ifPaidBy: string;
  payableWithDiscount: string;
  placeDate: string;
  contractor: string;
  client: string;
  customerReference: string;
  /** address of the customer on a document */
  customerAddress: string;
  /** contact person at the customer on a document */
  customerContact: string;
  signature: string;
  name: string;
  abschlag: string;
  phase: string;
  weight: string;
  fee: string;
  progress: string;
  earned: string;
  previous: string;
  current: string;
  extras: string;
  asOf: string;
  previousDocs: string;
  note: string;
  billingTypes: Record<BillingType, string>;
  statuses: Record<EntryStatus, string>;
}

export const REPORT_LABELS: Record<Language, ReportLabels> = {
  de: {
    date: 'Datum',
    week: 'KW',
    weekday: 'Wochentag',
    projectNumber: 'Projekt-Nr.',
    project: 'Projekt',
    taskId: 'Aufgaben-ID',
    task: 'Aufgabe',
    description: 'Beschreibung',
    hours: 'Stunden',
    billingType: 'Abrechnung',
    status: 'Status',
    location: 'Arbeitsort',
    person: 'Mitarbeiter/in',
    total: 'Gesamt',
    sum: 'Summe',
    perDay: 'Summe je Tag',
    perProject: 'Summe je Projekt',
    perArea: 'Summe je Bereich',
    area: 'Bereich',
    areaGroup: 'Gruppe',
    projectWork: 'Projektarbeit',
    internalWork: 'Intern',
    entries: 'Einträge',
    overview: 'Übersicht',
    period: 'Zeitraum',
    createdAt: 'Erstellt am',
    stundennachweis: 'Stundennachweis',
    customer: 'Auftraggeber',
    consultant: 'Auftragnehmer',
    partner: 'Partner',
    site: 'Einsatzort',
    activity: 'Tätigkeitsbeschreibung',
    totalHours: 'Summe Stunden',
    rate: 'Stundensatz',
    noRate: 'kein Stundensatz hinterlegt',
    amount: 'Betrag',
    net: 'Summe netto',
    vat: 'Umsatzsteuer',
    gross: 'Summe brutto',
    paymentTerms: 'Zahlungsbedingungen',
    payable: 'Zahlbetrag',
    discount: 'Skonto',
    ifPaidBy: 'bei Zahlung bis',
    payableWithDiscount: 'Zahlbetrag bei Skontoabzug',
    placeDate: 'Ort, Datum',
    contractor: 'Auftragnehmer',
    client: 'Auftraggeber',
    customerReference: 'Referenz Auftraggeber',
    customerAddress: 'Anschrift',
    customerContact: 'Ansprechpartner',
    signature: 'Unterschrift',
    name: 'Name',
    abschlag: 'Abschlagsrechnung',
    phase: 'Leistungsphase',
    weight: 'Bewertung %',
    fee: 'Honorar',
    progress: 'Leistungsstand %',
    earned: 'Erbrachte Leistung',
    previous: 'Bisher abgerechnet',
    current: 'Dieser Abschlag',
    extras: 'Weitere Positionen',
    asOf: 'Leistungsstand zum',
    previousDocs: 'Bisherige Abschläge',
    note: 'Hinweis',
    billingTypes: { billable: 'abrechenbar', included: 'im Pauschalhonorar', non_billable: 'nicht abrechenbar' },
    statuses: {
      draft: 'Entwurf',
      submitted: 'Eingereicht',
      approved_l1: 'Vom Projektleiter freigegeben',
      approved: 'Freigegeben',
      rejected: 'Zurückgewiesen',
      billed: 'Abgerechnet',
    },
  },
  en: {
    date: 'Date',
    week: 'CW',
    weekday: 'Weekday',
    projectNumber: 'Project no.',
    project: 'Project',
    taskId: 'Task ID',
    task: 'Task',
    description: 'Description',
    hours: 'Hours',
    billingType: 'Billing',
    status: 'Status',
    location: 'Work location',
    person: 'Employee',
    total: 'Total',
    sum: 'Sum',
    perDay: 'Total per day',
    perProject: 'Total per project',
    perArea: 'Total per area',
    area: 'Area',
    areaGroup: 'Group',
    projectWork: 'Project work',
    internalWork: 'Internal',
    entries: 'Entries',
    overview: 'Overview',
    period: 'Period',
    createdAt: 'Created on',
    stundennachweis: 'Timesheet',
    customer: 'Client',
    consultant: 'Contractor',
    partner: 'Partner',
    site: 'Site',
    activity: 'Description of work',
    totalHours: 'Total hours',
    rate: 'Hourly rate',
    noRate: 'no hourly rate stored',
    amount: 'Amount',
    net: 'Net total',
    vat: 'VAT',
    gross: 'Gross total',
    paymentTerms: 'Payment terms',
    payable: 'Amount payable',
    discount: 'Early payment discount',
    ifPaidBy: 'if paid by',
    payableWithDiscount: 'Amount payable after discount',
    placeDate: 'Place, date',
    contractor: 'Contractor',
    client: 'Client',
    customerReference: 'Client reference',
    customerAddress: 'Address',
    customerContact: 'Contact person',
    signature: 'Signature',
    name: 'Name',
    abschlag: 'Progress invoice',
    phase: 'Work stage',
    weight: 'Weight %',
    fee: 'Fee',
    progress: 'Progress %',
    earned: 'Earned',
    previous: 'Previously invoiced',
    current: 'This invoice',
    extras: 'Additional items',
    asOf: 'Progress as of',
    previousDocs: 'Previous progress invoices',
    note: 'Note',
    billingTypes: { billable: 'billable', included: 'included in fixed fee', non_billable: 'non-billable' },
    statuses: {
      draft: 'Draft',
      submitted: 'Submitted',
      approved_l1: 'Approved by project lead',
      approved: 'Approved',
      rejected: 'Rejected',
      billed: 'Billed',
    },
  },
  tr: {
    date: 'Tarih',
    week: 'Hafta',
    weekday: 'Gün',
    projectNumber: 'Proje no.',
    project: 'Proje',
    taskId: 'Görev ID',
    task: 'Görev',
    description: 'Açıklama',
    hours: 'Saat',
    billingType: 'Faturalandırma',
    status: 'Durum',
    location: 'Çalışma yeri',
    person: 'Çalışan',
    total: 'Toplam',
    sum: 'Toplam',
    perDay: 'Günlük toplam',
    perProject: 'Proje bazında toplam',
    perArea: 'Alan bazında toplam',
    area: 'Alan',
    areaGroup: 'Grup',
    projectWork: 'Proje çalışması',
    internalWork: 'Şirket içi',
    entries: 'Kayıtlar',
    overview: 'Özet',
    period: 'Dönem',
    createdAt: 'Oluşturulma',
    stundennachweis: 'Saat dökümü',
    customer: 'İşveren',
    consultant: 'Yüklenici',
    partner: 'Partner',
    site: 'Çalışma yeri',
    activity: 'Yapılan işler',
    totalHours: 'Toplam saat',
    rate: 'Saat ücreti',
    noRate: 'saat ücreti girilmemiş',
    amount: 'Tutar',
    net: 'Net toplam',
    vat: 'KDV',
    gross: 'Brüt toplam',
    paymentTerms: 'Ödeme koşulları',
    payable: 'Ödenecek tutar',
    discount: 'Erken ödeme iskontosu',
    ifPaidBy: 'şu tarihe kadar ödenirse:',
    payableWithDiscount: 'İskonto düşüldükten sonra ödenecek tutar',
    placeDate: 'Yer, tarih',
    contractor: 'Yüklenici',
    client: 'İşveren',
    customerReference: 'İşveren referansı',
    customerAddress: 'Adres',
    customerContact: 'İlgili kişi',
    signature: 'İmza',
    name: 'Ad soyad',
    abschlag: 'Hakediş',
    phase: 'Hizmet aşaması',
    weight: 'Ağırlık %',
    fee: 'Ücret',
    progress: 'İlerleme %',
    earned: 'Hak edilen',
    previous: 'Önceki hakedişler',
    current: 'Bu hakediş',
    extras: 'Ek kalemler',
    asOf: 'İlerleme tarihi',
    previousDocs: 'Önceki hakedişler',
    note: 'Not',
    billingTypes: { billable: 'faturalandırılır', included: 'götürü bedele dahil', non_billable: 'faturalandırılmaz' },
    statuses: {
      draft: 'Taslak',
      submitted: 'Gönderildi',
      approved_l1: 'Proje lideri onayladı',
      approved: 'Onaylandı',
      rejected: 'Reddedildi',
      billed: 'Faturalandı',
    },
  },
};

/** Labels of the absence list file (Abwesenheiten > Alle Abwesenheiten > Excel). */
export interface AbsenceReportLabels {
  sheet: string;
  person: string;
  kind: string;
  status: string;
  from: string;
  to: string;
  part: string;
  days: string;
  hours: string;
  note: string;
  decisionNote: string;
  decidedBy: string;
  decidedAt: string;
  requestedAt: string;
  source: string;
  halfDay: Record<'am' | 'pm', string>;
  kinds: Record<AbsenceKind | 'absent', string>;
  statuses: Record<AbsenceStatus, string>;
  sources: Record<string, string>;
}

export const ABSENCE_REPORT_LABELS: Record<Language, AbsenceReportLabels> = {
  de: {
    sheet: 'Abwesenheiten',
    person: 'Person',
    kind: 'Art',
    status: 'Status',
    from: 'Von',
    to: 'Bis',
    part: 'Halber Tag',
    days: 'Tage',
    hours: 'Stunden',
    note: 'Notiz',
    decisionNote: 'Begründung der Entscheidung',
    decidedBy: 'Entschieden von',
    decidedAt: 'Entschieden am',
    requestedAt: 'Beantragt am',
    source: 'Quelle',
    halfDay: { am: 'vormittags', pm: 'nachmittags' },
    kinds: {
      vacation: 'Urlaub',
      special: 'Sonderurlaub',
      training: 'Weiterbildung',
      overtime: 'Freizeitausgleich',
      unpaid: 'Unbezahlter Urlaub',
      sick: 'Krank',
      child_sick: 'Kind krank',
      parental: 'Elternzeit / Mutterschutz',
      other: 'Sonstige Abwesenheit',
      absent: 'Abwesend',
    },
    statuses: { requested: 'Beantragt', approved: 'Genehmigt', rejected: 'Abgelehnt', cancelled: 'Storniert' },
    sources: { self: 'Antrag', manual: 'Personal', import: 'Import' },
  },
  en: {
    sheet: 'Absences',
    person: 'Person',
    kind: 'Kind',
    status: 'Status',
    from: 'From',
    to: 'To',
    part: 'Half day',
    days: 'Days',
    hours: 'Hours',
    note: 'Note',
    decisionNote: 'Reason of the decision',
    decidedBy: 'Decided by',
    decidedAt: 'Decided on',
    requestedAt: 'Requested on',
    source: 'Source',
    halfDay: { am: 'morning', pm: 'afternoon' },
    kinds: {
      vacation: 'Vacation',
      special: 'Special leave',
      training: 'Training',
      overtime: 'Time off in lieu',
      unpaid: 'Unpaid leave',
      sick: 'Sick',
      child_sick: 'Child sick',
      parental: 'Parental / maternity leave',
      other: 'Other absence',
      absent: 'Absent',
    },
    statuses: { requested: 'Requested', approved: 'Approved', rejected: 'Rejected', cancelled: 'Cancelled' },
    sources: { self: 'Request', manual: 'HR', import: 'Import' },
  },
  tr: {
    sheet: 'Devamsızlıklar',
    person: 'Kişi',
    kind: 'Tür',
    status: 'Durum',
    from: 'Başlangıç',
    to: 'Bitiş',
    part: 'Yarım gün',
    days: 'Gün',
    hours: 'Saat',
    note: 'Not',
    decisionNote: 'Karar gerekçesi',
    decidedBy: 'Karar veren',
    decidedAt: 'Karar tarihi',
    requestedAt: 'Talep tarihi',
    source: 'Kaynak',
    halfDay: { am: 'öğleden önce', pm: 'öğleden sonra' },
    kinds: {
      vacation: 'İzin',
      special: 'Mazeret izni',
      training: 'Eğitim',
      overtime: 'Fazla mesai izni',
      unpaid: 'Ücretsiz izin',
      sick: 'Hastalık',
      child_sick: 'Çocuk hasta',
      parental: 'Doğum / ebeveyn izni',
      other: 'Diğer izin',
      absent: 'Ofis dışında',
    },
    statuses: { requested: 'Talep edildi', approved: 'Onaylandı', rejected: 'Reddedildi', cancelled: 'İptal edildi' },
    sources: { self: 'Talep', manual: 'Personel', import: 'İçe aktarma' },
  },
};
